import { BarChart3, CalendarCheck, Edit, ListChecks, Plus, Trash2, UserPlus } from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useDeleteTraining, useTrainings } from '@/hooks/use-trainings'
import { useTenantPath } from '@/lib/tenant'

export default function TrainingManagePage() {
  const tp = useTenantPath()
  const { data, isLoading } = useTrainings()
  const deleteTraining = useDeleteTraining()

  const trainings = Array.isArray(data) ? data : ((data as any)?.data ?? [])

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`「${title}」を削除しますか？`)) return
    try {
      await deleteTraining.mutateAsync(id)
      toast.success('研修を削除しました')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '削除に失敗しました')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">研修マスター管理</h1>
        <Skeleton className="h-96" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">研修マスター管理</h1>
        <Link to={tp('/teacher/trainings/new')}>
          <Button>
            <Plus className="mr-1 h-4 w-4" />
            新規作成
          </Button>
        </Link>
      </div>

      {trainings.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground">研修が登録されていません</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">研修一覧 ({trainings.length}件)</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>タイトル</TableHead>
                  <TableHead>タイプ</TableHead>
                  <TableHead>カテゴリ</TableHead>
                  <TableHead>公開</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trainings.map((t: any) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.title}</TableCell>
                    <TableCell>
                      <Badge variant={t.trainingType === 'open' ? 'secondary' : 'default'}>
                        {t.trainingType === 'open' ? '募集型' : 'アサイン型'}
                      </Badge>
                    </TableCell>
                    <TableCell>{t.category ?? '-'}</TableCell>
                    <TableCell>
                      <Badge variant={t.isPublished ? 'default' : 'outline'}>
                        {t.isPublished ? '公開' : '下書き'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Link to={tp(`/teacher/trainings/${t.id}/edit`)}>
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Link to={tp(`/teacher/trainings/${t.id}/assign`)}>
                          <Button variant="ghost" size="sm">
                            <UserPlus className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Link to={tp(`/teacher/trainings/${t.id}/attendance`)} title="出欠管理">
                          <Button variant="ghost" size="sm">
                            <CalendarCheck className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Link
                          to={tp(`/teacher/trainings/${t.id}/skill-items`)}
                          title="スキルチェック項目"
                        >
                          <Button variant="ghost" size="sm">
                            <ListChecks className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Link to={tp(`/teacher/training-reports/${t.id}`)}>
                          <Button variant="ghost" size="sm">
                            <BarChart3 className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(t.id, t.title)}
                          disabled={deleteTraining.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
